/**
 * SCQ funnel — Vidalytics loader + watch-progress gate (window.ScqVidalytics).
 *
 * Port of kk-headless src/lib/vidalytics/client.ts (warmVidalytics, loadVidalyticsEmbed,
 * attachVidalyticsProgress). Sentry calls become console.warn. Generic: the results page and the
 * order page's research VSL can both use it.
 *
 * The player object is reached via window._vidalytics.embeds[embedId].player (the documented
 * globals getVidalyticsPlayer() / window.vdb do not exist in this build). It exposes an event
 * emitter (.on/.off), currentTime()/duration() and the raw .mediaElement <video>.
 */
(function () {
  if (window.ScqVidalytics) return;

  var HOSTS = ['https://fast.vidalytics.com', 'https://api.vidalytics.com'];
  var EMBED_PREFIX = 'vidalytics_embed_';
  var loadedEmbeds = {};

  /** Warm DNS+TLS to the Vidalytics hosts (idempotent). */
  function warm() {
    try {
      HOSTS.forEach(function (href) {
        if (document.querySelector('link[rel="preconnect"][href="' + href + '"]')) return;
        var link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      });
    } catch (e) {
      /* resource hints are best-effort */
    }
  }

  function shortId(embedId) {
    return embedId.indexOf(EMBED_PREFIX) === 0 ? embedId.slice(EMBED_PREFIX.length) : embedId;
  }

  function loaderUrl(account, embedId) {
    return 'https://fast.vidalytics.com/embeds/' + account + '/' + shortId(embedId) + '/';
  }

  /**
   * Standard async embed snippet: loader.min.js -> player.min.js -> new Vidalytics.Embed().run(embedId).
   * Runs once per embedId per page life. Call after <div id="{embedId}"> is in the DOM.
   */
  function load(account, embedId) {
    if (loadedEmbeds[embedId]) return;
    loadedEmbeds[embedId] = true;
    var base = loaderUrl(account, embedId);
    try {
      var v = window;
      var d = 'Vidalytics';
      var y = '_' + d.toLowerCase();
      var c = d + 'L';
      if (!v[d]) v[d] = {};
      if (!v[c]) v[c] = {};
      if (!v[y]) v[y] = {};
      var vl = 'Loader';
      var vli = v[y][vl];
      var loaded = false;
      var inject = function (u, cb) {
        if (loaded) {
          cb();
          return;
        }
        var s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = u;
        s.onload = function () {
          loaded = true;
          cb();
        };
        document.getElementsByTagName('head')[0].appendChild(s);
      };
      inject(base + 'loader.min.js', function () {
        if (!vli) {
          vli = new v[c][vl]();
          v[y][vl] = vli;
        }
        vli.loadScript(base + 'player.min.js', function () {
          new v[d].Embed().run(embedId);
        });
      });
    } catch (err) {
      console.warn('[ScqVidalytics] embed load failed:', err);
    }
  }

  /**
   * Subscribe to watch progress for an embed. opts:
   *   onProgress(pct, currentTime, duration), onEnded(), onCtaShow(),
   *   onWatchTimeReached(), onUnavailable(reason), onMilestone(key, extra)
   *   initiallyUnlocked (bool), fallbackDurationSeconds, watchTimeUnlockSeconds,
   *   unlockPercent (default 50), milestoneKey (sessionStorage dedup key)
   * reason is one of: no-player | player-silent | player-stalled | unreadable-progress.
   * Returns a detach() function.
   */
  function attachProgress(embedId, opts) {
    opts = opts || {};
    var unlockPercent = typeof opts.unlockPercent === 'number' ? opts.unlockPercent : 50;

    var player = null;
    var cancelled = false;
    var pollTimer = null;
    var usingPlayerEvents = false;

    // Fail-open accounting: onUnavailable fires at most once from any path.
    var unavailableFired = false;
    var sawUsableProgress = false;
    var unreadableTicks = 0;
    var readThrewLogged = false;
    var UNREADABLE_TICK_LIMIT = 15; // ~3-4s of timeupdate

    // Post-attach liveness watchdog: player found but never emits a timeupdate -> fail OPEN.
    var sawAnyTick = false;
    var silentTimer = null;
    var SILENT_AFTER_ATTACH_MS = 30000;

    // Rolling stall watchdog, re-armed only on FORWARD progress. A deliberate pause (mediaElement.paused)
    // re-arms instead of firing, bounded by MAX_PAUSED_REARMS so a stuck-and-paused player still fails open.
    var unlocked = !!opts.initiallyUnlocked;
    var lastProgressCt = 0;
    var pausedRearms = 0;
    var MAX_PAUSED_REARMS = 10; // ~5.5 min
    var stallTimer = null;
    var STALL_AFTER_NO_PROGRESS_MS = 30000;

    // Wall-clock watch-time net (only counts gaps small enough to be continuous playback).
    var watchedMs = 0;
    var lastTickAt = 0;
    var watchTimeFired = false;
    var halfReachedByPercent = false;
    var MAX_TICK_GAP_MS = 2000;

    // Milestones deduped once per session (sessionStorage) + per attach (in-memory).
    var milestoneKey = opts.milestoneKey || 'scq_video_milestones_' + embedId;
    var seenMilestones = readMilestones();

    function readMilestones() {
      try {
        var raw = window.sessionStorage.getItem(milestoneKey);
        var arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        return [];
      }
    }

    function fireMilestone(key, extra) {
      if (seenMilestones.indexOf(key) !== -1) return;
      seenMilestones.push(key);
      try {
        window.sessionStorage.setItem(milestoneKey, JSON.stringify(seenMilestones));
      } catch (e) {
        /* session dedup is best-effort */
      }
      if (typeof opts.onMilestone === 'function') {
        try {
          opts.onMilestone(key, extra || {});
        } catch (e) {
          console.warn('[ScqVidalytics] onMilestone handler threw', e);
        }
      }
    }

    function clearStall() {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    }

    function armStall() {
      clearStall();
      if (unlocked || cancelled) return;
      stallTimer = setTimeout(function () {
        if (unlocked || cancelled) return;
        if (player && player.mediaElement && player.mediaElement.paused && pausedRearms++ < MAX_PAUSED_REARMS) {
          armStall();
          return;
        }
        fireUnavailable('player-stalled');
      }, STALL_AFTER_NO_PROGRESS_MS);
    }

    function fireUnavailable(reason) {
      if (unavailableFired || cancelled || unlocked) return;
      unavailableFired = true;
      unlocked = true;
      clearStall();
      try {
        if (opts.onUnavailable) opts.onUnavailable(reason);
      } catch (e) {
        console.warn('[ScqVidalytics] onUnavailable handler threw', e);
      }
    }

    function accrueWatchTime() {
      if (watchTimeFired || cancelled || halfReachedByPercent || !opts.watchTimeUnlockSeconds) return;
      var now = Date.now();
      if (lastTickAt > 0) {
        var delta = now - lastTickAt;
        if (delta > 0 && delta < MAX_TICK_GAP_MS) watchedMs += delta;
      }
      lastTickAt = now;
      if (watchedMs >= opts.watchTimeUnlockSeconds * 1000) {
        watchTimeFired = true;
        unlocked = true;
        clearStall();
        try {
          if (opts.onWatchTimeReached) opts.onWatchTimeReached();
        } catch (e) {
          console.warn('[ScqVidalytics] onWatchTimeReached handler threw', e);
        }
      }
    }

    function readProgress() {
      if (!player) return null;
      var ct = NaN;
      var dur = NaN;
      try {
        if (player.currentTime) ct = player.currentTime();
      } catch (e) {
        if (!readThrewLogged) {
          readThrewLogged = true;
          console.warn('[ScqVidalytics] player.currentTime() threw', e);
        }
      }
      try {
        if (player.duration) dur = player.duration();
      } catch (e) {
        if (!readThrewLogged) {
          readThrewLogged = true;
          console.warn('[ScqVidalytics] player.duration() threw', e);
        }
      }
      var media = player.mediaElement;
      if (isNaN(ct) && media) ct = media.currentTime;
      if ((isNaN(dur) || dur <= 0) && media) dur = media.duration;
      if ((isNaN(dur) || dur <= 0) && opts.fallbackDurationSeconds) dur = opts.fallbackDurationSeconds;
      if (isNaN(ct) || isNaN(dur) || dur <= 0) return null;
      return { ct: ct, dur: dur };
    }

    function handleTime() {
      if (cancelled) return;
      if (!sawAnyTick) {
        sawAnyTick = true;
        if (silentTimer) {
          clearTimeout(silentTimer);
          silentTimer = null;
        }
        armStall();
      }
      accrueWatchTime();
      if (watchTimeFired) {
        unlocked = true;
        clearStall();
      }
      var p = readProgress();
      if (!p) {
        if (!sawUsableProgress && ++unreadableTicks >= UNREADABLE_TICK_LIMIT) fireUnavailable('unreadable-progress');
        return;
      }
      if (p.ct > 0) sawUsableProgress = true;
      if (p.ct > lastProgressCt) {
        pausedRearms = 0;
        armStall();
      }
      lastProgressCt = p.ct;
      var pct = Math.min((p.ct / p.dur) * 100, 100);
      if (pct >= unlockPercent) {
        halfReachedByPercent = true;
        unlocked = true;
        clearStall();
      }
      fireMilestone('start', { duration: p.dur });
      if (pct >= 12.5) fireMilestone('12.5%');
      if (pct >= 25) fireMilestone('25%');
      if (pct >= 37.5) fireMilestone('37.5%');
      if (pct >= 50) fireMilestone('50%');
      if (pct >= 62.5) fireMilestone('62.5%');
      if (pct >= 75) fireMilestone('75%');
      if (pct >= 87.5) fireMilestone('87.5%');
      if (opts.onProgress) {
        try {
          opts.onProgress(pct, p.ct, p.dur);
        } catch (e) {
          console.warn('[ScqVidalytics] onProgress handler threw', e);
        }
      }
    }

    function handleEnded() {
      if (cancelled) return;
      unlocked = true;
      clearStall();
      fireMilestone('complete');
      if (opts.onEnded) {
        try {
          opts.onEnded();
        } catch (e) {
          console.warn('[ScqVidalytics] onEnded handler threw', e);
        }
      }
    }

    // Vidalytics native Timed-HTML-CTA reveal (dashboard-configured, targets #vslUnlockTrigger).
    function handleCtaShow() {
      if (cancelled) return;
      unlocked = true;
      clearStall();
      if (opts.onCtaShow) {
        try {
          opts.onCtaShow();
        } catch (e) {
          console.warn('[ScqVidalytics] onCtaShow handler threw', e);
        }
      }
    }

    function findPlayer() {
      try {
        var w = window._vidalytics;
        var p = w && w.embeds && w.embeds[embedId] && w.embeds[embedId].player;
        if (p && (typeof p.on === 'function' || p.mediaElement instanceof HTMLVideoElement)) return p;
        return null;
      } catch (e) {
        return null;
      }
    }

    var attempts = 0;
    function tryAttach() {
      if (cancelled) return;
      var p = findPlayer();
      if (p) {
        player = p;
        if (typeof p.on === 'function') {
          usingPlayerEvents = true;
          p.on('timeupdate', handleTime);
          p.on('ended', handleEnded);
          p.on('cta:show', handleCtaShow);
        } else if (p.mediaElement) {
          p.mediaElement.addEventListener('timeupdate', handleTime);
          p.mediaElement.addEventListener('ended', handleEnded);
        }
        silentTimer = setTimeout(function () {
          if (!sawAnyTick && !unlocked) fireUnavailable('player-silent');
        }, SILENT_AFTER_ATTACH_MS);
        return;
      }
      // Poll ~30s (60 x 500ms) for player init; if it never appears, fail OPEN.
      if (attempts++ < 60) {
        pollTimer = setTimeout(tryAttach, 500);
      } else if (!unlocked) {
        fireUnavailable('no-player');
      }
    }
    tryAttach();

    return function detach() {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (silentTimer) clearTimeout(silentTimer);
      clearStall();
      if (!player) return;
      if (usingPlayerEvents) {
        if (typeof player.off === 'function') {
          player.off('timeupdate', handleTime);
          player.off('ended', handleEnded);
          player.off('cta:show', handleCtaShow);
        }
      } else if (player.mediaElement) {
        player.mediaElement.removeEventListener('timeupdate', handleTime);
        player.mediaElement.removeEventListener('ended', handleEnded);
      }
    };
  }

  window.ScqVidalytics = {
    warm: warm,
    load: load,
    loaderUrl: loaderUrl,
    shortId: shortId,
    attachProgress: attachProgress,
  };
})();
